import { useState, useEffect, useContext, useCallback } from 'react';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';

/**
 * Hook for managing changelog data
 * Provides functionality to load, filter, and manage changelog entries
 */
// Simple in-memory cache to avoid duplicate fetches (also prevents React StrictMode double fetch in dev)
let changelogCache = null;
let changelogLoaded = false;
let changelogLoadingPromise = null;

const shouldIncludeUnpublishedEntries =
  Boolean(import.meta.env.DEV) ||
  import.meta.env.VITE_IS_DEV === 'true' ||
  import.meta.env.VITE_IS_STAGING === 'true';

const filterPublicChangelogEntries = changelogData => {
  if (!changelogData || !Array.isArray(changelogData.changelog)) {
    return { changelog: [] };
  }

  return {
    ...changelogData,
    changelog: changelogData.changelog.filter(
      entry => shouldIncludeUnpublishedEntries || entry?.public !== false
    ),
  };
};

function useChangelog() {
  const [changelogData, setChangelogData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { sendRequest } = useHttp();
  const authCtx = useContext(AuthContext);

  useEffect(() => {
    let isActive = true;

    const loadChangelog = async () => {
      try {
        setLoading(true);
        setError(null);

        // Serve from cache if already loaded
        if (changelogLoaded && changelogCache) {
          if (isActive) setChangelogData(changelogCache);
          return;
        }

        // If a request is already in-flight, await it instead of issuing another
        if (changelogLoadingPromise) {
          const result = filterPublicChangelogEntries(await changelogLoadingPromise);
          if (isActive) setChangelogData(result);
          return;
        }

        // Start a new request
        const headers = authCtx.getAuthHeaders();
        changelogLoadingPromise = sendRequest({
          url: '/api/v1/changelog',
          method: 'GET',
          headers,
        });

        const result = filterPublicChangelogEntries(await changelogLoadingPromise);

        if (result && result.changelog) {
          changelogCache = result;
          changelogLoaded = true;
          if (isActive) setChangelogData(result);
        } else {
          throw new Error('Invalid response format from changelog API');
        }
      } catch (err) {
        console.error('Error loading changelog:', err);
        if (isActive) setError(err.message);
      } finally {
        if (isActive) setLoading(false);
        changelogLoadingPromise = null;
      }
    };

    loadChangelog();

    return () => {
      isActive = false;
    };
  }, []);

  /**
   * Get all changelog entries sorted by date (newest first)
   */
  const getAllEntries = useCallback(() => {
    if (!changelogData?.changelog) return [];
    return [...changelogData.changelog].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [changelogData]);

  /**
   * Get changelog entries filtered by repository
   */
  const getEntriesByRepository = useCallback(
    repository => {
      const allEntries = getAllEntries();
      return allEntries.filter(
        entry => entry.repositories && entry.repositories.includes(repository)
      );
    },
    [getAllEntries]
  );

  /**
   * Get changelog entries filtered by date range
   */
  const getEntriesByDateRange = useCallback(
    (startDate, endDate) => {
      const allEntries = getAllEntries();
      return allEntries.filter(entry => {
        const entryDate = new Date(entry.date);
        return entryDate >= new Date(startDate) && entryDate <= new Date(endDate);
      });
    },
    [getAllEntries]
  );

  /**
   * Get the latest changelog entry
   */
  const getLatestEntry = useCallback(() => {
    const allEntries = getAllEntries();
    return allEntries.length > 0 ? allEntries[0] : null;
  }, [getAllEntries]);

  /**
   * Get changelog entries for a specific version
   */
  const getEntryByVersion = useCallback(
    version => {
      const allEntries = getAllEntries();
      return allEntries.find(entry => entry.version === version);
    },
    [getAllEntries]
  );

  /**
   * Get all unique repositories mentioned in changelog
   */
  const getAllRepositories = useCallback(() => {
    if (!changelogData?.changelog) return [];
    const repositories = new Set();
    changelogData.changelog.forEach(entry => {
      if (entry.repositories) {
        entry.repositories.forEach(repo => repositories.add(repo));
      }
    });
    return Array.from(repositories);
  }, [changelogData]);

  /**
   * Get statistics about changelog entries
   */
  const getChangelogStats = useCallback(() => {
    const allEntries = getAllEntries();
    const repositories = getAllRepositories();

    return {
      totalEntries: allEntries.length,
      totalRepositories: repositories.length,
      latestVersion: allEntries.length > 0 ? allEntries[0].version : null,
      latestDate: allEntries.length > 0 ? allEntries[0].date : null,
      repositories: repositories,
    };
  }, [getAllEntries, getAllRepositories]);

  return {
    changelogData,
    loading,
    error,
    getAllEntries,
    getEntriesByRepository,
    getEntriesByDateRange,
    getLatestEntry,
    getEntryByVersion,
    getAllRepositories,
    getChangelogStats,
  };
}

export default useChangelog;
